/*
  Reference link: https://shopify.dev/docs/themes/navigation-search/search/predictive-search
*/
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.liveResultsPanel = this.querySelector("[data-lp-predictive-live]");
    this.idleResultsPanel = this.querySelector("[data-lp-predictive-idle]");
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
    this.updateViewAllLink();
    this.syncLpProductGridLimits();

    if (this.isLpHeaderSearch()) {
      this.lpLayoutMq = window.matchMedia("(max-width: 988px)");
      this.onLpLayoutChange = () => {
        this.syncLpProductGridLimits();
        this.applyLiveProductImageWidths(this.liveResultsPanel);
        this.updateViewAllLink();
      };
      this.lpLayoutMq.addEventListener("change", this.onLpLayoutChange);
    }
  }

  isLpMobileLayout() {
    return (
      this.isLpHeaderSearch() &&
      window.matchMedia("(max-width: 988px)").matches
    );
  }

  getLpProductLimit() {
    const desktop = parseInt(this.dataset.productLimit, 10) || 4;
    const mobile = parseInt(this.dataset.productLimitMobile, 10) || desktop;
    return this.isLpMobileLayout() ? mobile : desktop;
  }

  getLpProductImageMax() {
    const desktop = parseInt(this.dataset.productImageMax, 10) || 500;
    const mobile = parseInt(this.dataset.productImageMaxMobile, 10) || desktop;
    return this.isLpMobileLayout() ? mobile : desktop;
  }

  syncLpProductGridLimits(root) {
    if (!this.isLpHeaderSearch()) return;

    const limit = this.getLpProductLimit();
    const scope = root || this;
    scope
      .querySelectorAll(".lp-predictive-split__products-grid .lp-predictive-product")
      .forEach((el, index) => {
        el.hidden = index >= limit;
      });
  }

  updateViewAllLink() {
    if (!this.isLpHeaderSearch()) return;

    const footer =
      this.viewAllFooter ||
      this.querySelector("[data-lp-predictive-view-all]");
    const link =
      this.viewAllLink ||
      this.querySelector("[data-lp-predictive-view-all-link]");
    this.viewAllFooter = footer;
    this.viewAllLink = link;

    const form = this.input?.form;
    const action = (form?.action || "/search").split("?")[0];
    const params = new URLSearchParams();
    params.set("type", "product");
    params.set("options[prefix]", "last");
    params.set("options[unavailable_products]", "last");
    const term = this.getQuery();
    if (term) params.set("q", term);
    const searchUrl = `${action}?${params.toString()}`;

    if (footer && link) {
      link.href = searchUrl;
      footer.hidden = !this.hasAttribute("open") || !term;
    }

    this.updateSearchForLink(searchUrl, term);
  }

  updateSearchForLink(searchUrl, term) {
    if (!this.isLpHeaderSearch()) return;

    const wrap =
      this.searchForWrap ||
      this.querySelector("[data-lp-predictive-search-for-wrap]");
    const link =
      this.searchForLink ||
      this.querySelector("[data-lp-predictive-search-for-link]");
    const textEl = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    this.searchForWrap = wrap;
    this.searchForLink = link;
    if (!wrap || !link || !textEl) return;

    const isDesktop = window.matchMedia("(min-width: 989px)").matches;
    const activeTerm = term ?? this.getQuery();
    const searchUiOpen =
      this.hasAttribute("open") ||
      (isDesktop &&
        document.body.classList.contains("lp-search-desktop-active")) ||
      document.body.classList.contains("lp-search-open");
    wrap.hidden = !isDesktop || !searchUiOpen || !activeTerm;

    if (!activeTerm) {
      textEl.textContent = "";
      return;
    }

    link.href = searchUrl || link.href;
    const template = this.dataset.searchForTemplate || 'Search for "@@TERM@@"';
    textEl.textContent = template.replace("@@TERM@@", activeTerm);
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");
    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;
    this.updateViewAllLink();

    if (!this.searchTerm.length) {
      if (this.isLpHeaderSearch()) {
        this.showIdleState();
        this.open();
      } else {
        this.close(true);
      }
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  isLpHeaderSearch() {
    return this.dataset.lpHeaderSearch === "true";
  }

  showIdleState() {
    if (!this.isLpHeaderSearch() || !this.idleResultsPanel) return;

    this.removeAttribute("loading");
    this.removeAttribute("results");
    if (this.liveResultsPanel) {
      this.liveResultsPanel.innerHTML = "";
      this.liveResultsPanel.hidden = true;
    }
    this.idleResultsPanel.hidden = false;
    document.dispatchEvent(new CustomEvent("lp:predictive-idle-open"));
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.syncLpProductGridLimits(this.idleResultsPanel);
    this.updateViewAllLink();
  }

  hideIdleState() {
    if (this.idleResultsPanel) {
      this.idleResultsPanel.hidden = true;
    }
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      if (this.isLpHeaderSearch()) {
        this.input.value = "";
        this.removeAttribute("results");
        this.showIdleState();
        this.open();
      } else {
        this.closeResults(true);
      }
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.isLpHeaderSearch()) {
        this.showIdleState();
        this.open();
      }
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.isLpHeaderSearch() && window.matchMedia("(min-width: 989px)").matches) {
        const layer = document.getElementById("lp-search-layer");
        if (
          document.body.classList.contains("lp-search-desktop-active") &&
          (layer?.contains(document.activeElement) ||
            document.activeElement === this.input)
        ) {
          return;
        }
        if (Date.now() < (window.__lpDesktopSearchOpenGuard || 0)) {
          return;
        }
      }

      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      if (this.isLpHeaderSearch()) {
        this.showIdleState();
        this.open();
      } else {
        this.close(true);
      }
      return;
    }

    switch (event.code) {
      case "ArrowUp":
        event.preventDefault();
        this.switchOption("up");
        break;
      case "ArrowDown":
        event.preventDefault();
        this.switchOption("down");
        break;
      case "Enter":
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    if (!searchForTextElement) return;

    const template =
      this.dataset.searchForTemplate || 'Search for "@@TERM@@"';
    if (previousTerm && searchForTextElement.textContent.includes(previousTerm)) {
      searchForTextElement.textContent = template.replace("@@TERM@@", newTerm);
      return;
    }

    searchForTextElement.textContent = template.replace("@@TERM@@", newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterTypeQuery = this.getSearchFilterTypeQuery();
    // const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const queryKey =
      searchTerm.replaceAll(" ", "-").toLowerCase() +
      "-" +
      filterType +
      "-" +
      filterTypeQuery.replaceAll(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const limit = this.dataset.limit;
    const resultTypes = this.dataset.types;
    const showUnavailable = this.dataset.unavailable;
    const query = filterTypeQuery
      ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`)
      : encodeURIComponent(searchTerm);

    const sectionId = this.isLpHeaderSearch()
      ? "lp-predictive-search-split"
      : "predictive-search";
    const productLimit =
      parseInt(this.dataset.productFetchLimit, 10) ||
      parseInt(this.dataset.productLimit, 10) ||
      parseInt(limit, 10) ||
      4;
    const suggestionLimit =
      parseInt(this.dataset.suggestionLimit, 10) || 10;
    const fetchLimit = this.isLpHeaderSearch()
      ? Math.max(productLimit, suggestionLimit)
      : limit;

    fetch(
      `${routes.predictive_search_url}?q=${query}&resources[type]=${resultTypes}&resources[limit]=${fetchLimit}&options[prefix]=last&options[unavailable_products]=${showUnavailable}&section_id=${sectionId}`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const sectionRoot = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(`#shopify-section-${sectionId}`);
        const resultsMarkup = sectionRoot?.innerHTML?.trim() || "";
        if (!resultsMarkup && this.isLpHeaderSearch()) {
          this.close();
          return;
        }
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  syncGreyProductCards(root) {
    if (!root || this.dataset.greyProductImages !== "false") return;
    root.querySelectorAll(".lp-predictive-product--grey").forEach((el) => {
      el.classList.remove("lp-predictive-product--grey");
    });
  }

  syncLiveProductMeta(root) {
    if (!root || !this.isLpHeaderSearch()) return;

    if (this.dataset.showPrice === "false") {
      root.querySelectorAll(".lp-predictive-product__price").forEach((el) => {
        el.remove();
      });
    }

    if (this.dataset.showVendor === "false") {
      root.querySelectorAll(".lp-predictive-product__vendor").forEach((el) => {
        el.remove();
      });
    }
  }

  applyLiveProductImageWidths(root) {
    if (!root || !this.isLpHeaderSearch()) return;
    const width = this.getLpProductImageMax();
    if (!width) return;

    root.querySelectorAll(".lp-predictive-product__media img").forEach((img) => {
      if (!img.currentSrc && !img.src) return;
      try {
        const url = new URL(img.currentSrc || img.src, window.location.origin);
        url.searchParams.set("width", String(width));
        const nextSrc = url.toString();
        if (img.src !== nextSrc) img.src = nextSrc;
      } catch (_) {
        /* ignore malformed image URLs */
      }
    });
  }

  renderSearchResults(resultsMarkup) {
    if (this.liveResultsPanel) {
      this.liveResultsPanel.innerHTML = resultsMarkup;
      this.syncGreyProductCards(this.liveResultsPanel);
      this.syncLiveProductMeta(this.liveResultsPanel);
      this.applyLiveProductImageWidths(this.liveResultsPanel);
      this.syncLpProductGridLimits(this.liveResultsPanel);
      this.liveResultsPanel.hidden = false;
      this.hideIdleState();
    } else {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
    this.updateViewAllLink();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]"
    );
    if (!countElement) return;
    this.setLiveRegionText(countElement.textContent);
  }

  getResultsMaxHeight() {
    if (
      this.isLpHeaderSearch() &&
      window.matchMedia("(min-width: 989px)").matches
    ) {
      this.resultsMaxHeight = false;
      return false;
    }

    if (this.isLpHeaderSearch()) {
      if (document.body.classList.contains("lp-search-open")) {
        this.resultsMaxHeight = false;
        return false;
      }
      this.resultsMaxHeight = Math.round(window.innerHeight * 0.8);
      return this.resultsMaxHeight;
    }

    const headerAnchor =
      document.querySelector(".lp-header-section") ||
      document.querySelector(".header-default");
    const bottom = headerAnchor
      ? headerAnchor.getBoundingClientRect().bottom
      : 120;
    this.resultsMaxHeight = Math.max(240, window.innerHeight - bottom - 16);
    return this.resultsMaxHeight;
  }

  open() {
    const maxHeight = this.getResultsMaxHeight();
    if (maxHeight) {
      this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
    } else {
      this.predictiveSearchResults.removeAttribute("style");
    }
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    this.updateViewAllLink();
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    if (this.liveResultsPanel) {
      this.liveResultsPanel.innerHTML = "";
      this.liveResultsPanel.hidden = true;
    }
    if (this.idleResultsPanel) {
      this.idleResultsPanel.hidden = true;
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isLpHeaderSearch()) {
      document.dispatchEvent(new CustomEvent("lp:predictive-close"));
    }
    this.updateViewAllLink();
  }
}

customElements.define("predictive-search", PredictiveSearch);
